/* Dialog showing details about a user's source: name, type, URL, tags, item count, and creation date.
   Opened by clicking a source name in feed items. Uses only local DB data (no external API calls). */

import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  Youtube,
  Rss,
  Podcast,
  Instagram,
  Mail,
  ExternalLink,
  Copy,
  Check,
  Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import type { SourceType } from "./SourceItem";

const SOURCE_TYPE_CONFIG: Record<
  SourceType,
  { label: string; icon: React.ElementType }
> = {
  youtube: { label: "YouTube Channel", icon: Youtube },
  rss: { label: "RSS Feed", icon: Rss },
  podcast: { label: "Podcast", icon: Podcast },
  instagram: { label: "Instagram", icon: Instagram },
  email: { label: "Newsletter", icon: Mail },
};

interface SourceInfoDialogProps {
  sourceId: Id<"sources"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SourceInfoDialog({ sourceId, open, onOpenChange }: SourceInfoDialogProps) {
  const source = useQuery(
    api.sources.getDetail,
    sourceId ? { id: sourceId } : "skip",
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0">
        {!source ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <SourceInfoContent source={source} />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface SourceDetail {
  name: string;
  type: string;
  url: string;
  tags?: string[];
  starred?: boolean;
  muted?: boolean;
  createdAt: number;
  skipProvocationCheck?: boolean;
  itemCount: number;
}

function SourceInfoContent({ source }: { source: SourceDetail }) {
  const config = SOURCE_TYPE_CONFIG[source.type as SourceType];
  const Icon = config.icon;
  const [urlCopied, setUrlCopied] = useState(false);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(source.url);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const createdDate = new Date(source.createdAt).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <>
      {/* Header */}
      <div className="bg-muted/40 px-5 pt-5 pb-4">
        <div className="flex items-center gap-3.5">
          <div className="size-12 rounded-full bg-muted flex items-center justify-center ring-2 ring-background shadow-md shrink-0">
            <Icon className="size-6 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold tracking-tight truncate text-foreground">
              {source.name}
            </h2>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Icon className="size-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{config.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 py-4 space-y-4">
        <div className="rounded-lg bg-muted/40 ring-1 ring-border/50 flex divide-x divide-border/50">
          <div className="flex-1 text-center py-2.5">
            <p className="text-base font-semibold tabular-nums text-foreground">
              {source.itemCount}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">items</p>
          </div>
          <div className="flex-1 text-center py-2.5">
            <p className="text-base font-semibold text-foreground">{createdDate}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">added</p>
          </div>
        </div>

        {/* URL */}
        <div className="flex items-center gap-2 rounded-lg bg-muted/40 ring-1 ring-border/50 px-3 py-2">
          <p className="text-xs text-muted-foreground truncate flex-1 min-w-0">
            {source.url}
          </p>
          <Button
            variant="ghost"
            size="icon-xs"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={handleCopyUrl}
            title="Copy URL"
          >
            {urlCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          </Button>
        </div>

        {/* Tags */}
        {source.tags && source.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {source.tags.map((tag) => (
              <Badge key={tag} variant="secondary">
                #{tag}
              </Badge>
            ))}
          </div>
        )}

        <Separator />

        {/* Status indicators */}
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          {source.starred && (
            <span className="text-primary font-medium">Starred</span>
          )}
          {source.muted && (
            <span>Muted</span>
          )}
          {source.skipProvocationCheck && (
            <span>Provocative filter excluded</span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-4 flex items-center gap-2 border-t border-border/50">
        <Button variant="outline" size="sm" className="flex-1" asChild>
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit <ExternalLink className="size-3 ml-1" />
          </a>
        </Button>
      </div>
    </>
  );
}
